/**
 * 서버 fill 을 "지금도 빈" 슬롯에만 매핑한다.
 *
 * 자리는 인덱스가 아니라 **슬롯 id** 로 찾는다 — 응답을 기다리는 사이 사용자가
 * 선지를 재정렬·삽입·삭제하면 인덱스는 다른 슬롯을 가리키기 때문이다. id 가
 * 사라졌으면(삭제된 슬롯) 조용히 건너뛴다.
 */
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { toQuizPlainText } from '../../ai-builder-chat/utils/adaptAiGeneratedQuizzesToBuildForm';

import { isSlotFilled, type Slot, slotsOf } from './questionCardAiSlotSchema';

export type OptionFillWrite = { slotId: string; index: number; text: string };
export type OptionFillResult = { filled: OptionFillWrite[]; skipped: string[] };

type FillCandidate = {
  text: string;
  slot: Slot | undefined;
  hasServerAnswerPick: boolean;
};

/**
 * 쓰지 않는 조건 — 하나라도 맞으면 그 fill 은 건너뛴다.
 *
 * `answerSlot`: 오답만 받아온 요청에서는 적용 시점 정답 선지를 건너뛴다 — 요청 중 사용자가
 * 빈 선지를 정답으로 골랐다면 그 자리에 오답을 쓰게 되기 때문이다. 후보 모드(서버가 정답까지
 * 만들어 지목한 경우)는 예외다. 그때는 정답 슬롯도 우리가 채울 대상이고, 빈 카드는 0번이
 * 기본 정답으로 찍혀 있다.
 */
const FILL_REJECTIONS: Record<string, (candidate: FillCandidate) => boolean> = {
  // 폼에는 평문 정규화(writeOptionFills)를 거쳐 쓰이므로, 마크만 있는 값도 빈 값이다.
  emptyText: ({ text }) => !toQuizPlainText(text).trim(),
  slotRemoved: ({ slot }) => !slot,
  slotFilled: ({ slot }) => !!slot && isSlotFilled(slot),
  answerSlot: ({ slot, hasServerAnswerPick }) =>
    !hasServerAnswerPick && !!slot?.isAnswer,
};

export const applyOptionFill = (
  quiz: BuildQuizListItem,
  fills: { slot_id: string; text: string }[],
  hasServerAnswerPick = false,
): OptionFillResult => {
  const slots = slotsOf(quiz);
  const filled: OptionFillWrite[] = [];
  const skipped: string[] = [];

  for (const fill of fills) {
    const index = slots.findIndex(slot => slot.key === fill.slot_id);
    const candidate = {
      text: fill.text.trim(),
      slot: slots[index],
      hasServerAnswerPick,
    };
    if (Object.values(FILL_REJECTIONS).some(rejects => rejects(candidate)))
      skipped.push(fill.slot_id);
    else filled.push({ slotId: fill.slot_id, index, text: candidate.text });
  }

  return { filled, skipped };
};
